import React, { useEffect } from 'react'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'

import { renderTextWithMarks } from '@/components/comments/renderHighlightedText'
import { findScrollParent, placeArmedCue } from '@/components/comments/armedCue'

import { composeSegmentsText, segmentMarks } from '../promptOffsets'

import SystemSegmentBar from './SystemSegmentBar'

// Renders a prompt's segmented content. Visible segments display as plain
// text; system segments render as collapsed gray bars with an expand toggle —
// the content is read-only when expanded (PE never edits inside <system>).
//
// Falls back to plain `content` (treated as one visible block) when the BE
// response doesn't include a segments array.

// A visible segment is "noise" when it contains nothing but ${var_name}
// references + whitespace (e.g. a trailing `${customer_context}` block).
// The substitution happens at runtime — surfacing the placeholder alone in
// the read-only display adds visual weight without helping a human reader.
// System segments are never filtered: their content is semantically
// meaningful (tool contracts, hidden instructions) even if it parses as
// just variables.
const VAR_REF_RE = /\$\{[A-Za-z_][A-Za-z0-9_]*\}/g
const isNoiseSegment = (segment) => {
  if (!segment || segment.type !== 'visible') {
    return false
  }
  const stripped = (segment.content || '').replace(VAR_REF_RE, '').trim()
  return isEmpty(stripped)
}

/**
 * @param {boolean} [props.armed] the comment flow is waiting for a span —
 *   ring the commentable blocks and run the select-this cue over them
 * @param {object[]} props.segments ordered prompt segments
 * @param {string} [props.fallbackText] whole prompt when there are no segments
 * @param {object} [props.containerRef] ref to the root, for the selection popover
 * @param {object[]} [props.marks] re-anchored comment marks (`[{id,start,end}]`, absolute in base text)
 * @param {number} [props.focusedRequestId] comment to render as the active mark
 * @param {Function} [props.onCommentClick] (requestId) => void when a mark is clicked
 */
const SegmentedPromptDisplay = ({
  segments,
  fallbackText,
  containerRef,
  marks = [],
  focusedRequestId,
  onCommentClick,
  armed = false,
}) => {
  // Keep the armed cue on the line being read. Pinned to the top of the block
  // it would sit thousands of pixels above the panel on a long prompt —
  // rendered, but never seen, which is what "the highlight doesn't work" meant.
  useEffect(() => {
    const container = containerRef?.current
    if (!armed || isNil(container)) {
      return undefined
    }
    const scroller = findScrollParent(container)
    const place = () =>
      container
        .querySelectorAll('.comment-armed')
        .forEach((block) => placeArmedCue(block, scroller))
    place()
    scroller?.addEventListener('scroll', place, { passive: true })
    window.addEventListener('resize', place)
    return () => {
      scroller?.removeEventListener('scroll', place)
      window.removeEventListener('resize', place)
    }
  }, [armed, containerRef])

  // Bring the focused comment's line into view. A prompt runs to thousands of
  // pixels, so "which line is this about" is not a question the reader can
  // answer by scrolling — landing on the mark is the whole point of clicking
  // the comment. `inline: 'nearest'` so a wide block can't scroll sideways and
  // carry the panel's content off with it.
  useEffect(() => {
    if (isNil(focusedRequestId) || isNil(containerRef?.current)) {
      return
    }
    const mark = containerRef.current.querySelector(
      `[data-testid="comment-mark-${focusedRequestId}"]`
    )
    mark?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
  }, [focusedRequestId, containerRef])
  const rawList = !isEmpty(segments)
    ? segments
    : [{ index: 0, type: 'visible', content: fallbackText || '' }]
  const list = rawList.filter((segment) => !isNoiseSegment(segment))
  const { baseByIndex } = composeSegmentsText(segments, fallbackText)

  // Absolute comment offsets → this segment's local marks. data-segment-index
  // lets the selection popover map a selection back to its absolute offset.
  const renderSegmentText = (segment) =>
    renderTextWithMarks(
      segment.content,
      segmentMarks(marks, segment, baseByIndex, focusedRequestId),
      {
        onMarkClick: onCommentClick,
      }
    )

  // Nothing renderable (a peeked empty/reset/revert version, or a genuinely
  // empty active prompt). Show an explicit hint instead of a blank box so it
  // reads as "this version is empty", not broken.
  if (isEmpty(list)) {
    return (
      <div className="voice-agents__segmented-prompt" data-testid="segmented-prompt">
        <p className="voice-agents__drawer-tab-hint" data-testid="segmented-prompt-empty">
          This version has no prompt content.
        </p>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="voice-agents__segmented-prompt"
      data-testid="segmented-prompt"
    >
      {list.map((segment) => {
        if (segment.type === 'visible') {
          return (
            <pre
              key={segment.index}
              className={classNames('voice-agents__segmented-prompt-visible', {
                // Armed for commenting: ring + sweep, the same cue the
                // transcript shows. On the editable blocks only — the system
                // segments are not commentable.
                'comment-armed': armed,
              })}
              data-testid={`segment-visible-${segment.index}`}
              data-segment-index={segment.index}
            >
              {renderSegmentText(segment)}
            </pre>
          )
        }
        return (
          <SystemSegmentBar key={segment.index} segment={segment}>
            {renderSegmentText(segment)}
          </SystemSegmentBar>
        )
      })}
    </div>
  )
}

export default SegmentedPromptDisplay
